/*
 * Copyright (c) 2026 NOOGA DEVELOPMENT, All rights reserved.
 */

import { enUS, ruRU } from '@mui/x-date-pickers/locales';
import { LocaleId } from 'data/translation/i18n';
import moment from 'moment';
import 'moment/locale/az';
import 'moment/locale/ru';

type DatePickerLocaleText = typeof enUS.components.MuiLocalizationProvider.defaultProps.localeText;

// Keep the calendar data on the same Moment instance that is passed to AdapterMoment.
// This mirrors updateMomentLocales approach and avoids relying on Vite
// preserving side-effect locale registration across optimized chunks.
moment.updateLocale(LocaleId.Ru, {
    week: { dow: 1, doy: 4 },
    months: [
        'январь',
        'февраль',
        'март',
        'апрель',
        'май',
        'июнь',
        'июль',
        'август',
        'сентябрь',
        'октябрь',
        'ноябрь',
        'декабрь',
    ],
    monthsShort: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
    weekdays: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'],
    weekdaysShort: ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'],
    weekdaysMin: ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'],
});

moment.updateLocale(LocaleId.Az, {
    week: { dow: 1, doy: 7 },
    months: [
        'yanvar',
        'fevral',
        'mart',
        'aprel',
        'may',
        'iyun',
        'iyul',
        'avqust',
        'sentyabr',
        'oktyabr',
        'noyabr',
        'dekabr',
    ],
    monthsShort: ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avq', 'sen', 'okt', 'noy', 'dek'],
    weekdays: ['Bazar', 'Bazar ertəsi', 'Çərşənbə axşamı', 'Çərşənbə', 'Cümə axşamı', 'Cümə', 'Şənbə'],
    weekdaysShort: ['Baz', 'BzE', 'ÇAx', 'Çər', 'CAx', 'Cüm', 'Şən'],
    weekdaysMin: ['Bz', 'BE', 'ÇA', 'Çə', 'CA', 'Cü', 'Şə'],
});

const azTimeViews = {
    hours: 'saat',
    minutes: 'dəqiqə',
    seconds: 'saniyə',
    meridiem: 'gün hissəsi',
};

export const azAZDatePickerLocaleText: DatePickerLocaleText = {
    previousMonth: 'Əvvəlki ay',
    nextMonth: 'Növbəti ay',
    openPreviousView: 'Əvvəlki görünüşü aç',
    openNextView: 'Növbəti görünüşü aç',
    calendarViewSwitchingButtonAriaLabel: (view) =>
        view === 'year'
            ? 'İl görünüşü açıqdır, təqvim görünüşünə keçin'
            : 'Təqvim görünüşü açıqdır, il görünüşünə keçin',
    start: 'Başlanğıc',
    end: 'Son',
    startDate: 'Başlanğıc tarixi',
    startTime: 'Başlanğıc vaxtı',
    endDate: 'Son tarix',
    endTime: 'Son vaxt',
    cancelButtonLabel: 'Ləğv et',
    clearButtonLabel: 'Təmizlə',
    okButtonLabel: 'Təsdiq et',
    todayButtonLabel: 'Bu gün',
    datePickerToolbarTitle: 'Tarix seçin',
    dateTimePickerToolbarTitle: 'Tarix və vaxt seçin',
    timePickerToolbarTitle: 'Vaxt seçin',
    dateRangePickerToolbarTitle: 'Tarix aralığını seçin',
    clockLabelText: (view, _time, _utils, formattedTime) =>
        `${azTimeViews[view]} seçin. ${formattedTime ? `Seçilmiş vaxt: ${formattedTime}` : 'Vaxt seçilməyib'}`,
    hoursClockNumberText: (hours) => `${hours} saat`,
    minutesClockNumberText: (minutes) => `${minutes} dəqiqə`,
    secondsClockNumberText: (seconds) => `${seconds} saniyə`,
    selectViewText: (view) => `${azTimeViews[view]} seçin`,
    calendarWeekNumberHeaderLabel: 'Həftə nömrəsi',
    calendarWeekNumberHeaderText: '№',
    calendarWeekNumberAriaLabelText: (weekNumber) => `${weekNumber}-ci həftə`,
    calendarWeekNumberText: (weekNumber) => `${weekNumber}`,
    openDatePickerDialogue: (_value, _utils, formattedDate) =>
        formattedDate ? `Tarix seçin, seçilmiş tarix: ${formattedDate}` : 'Tarix seçin',
    openTimePickerDialogue: (_value, _utils, formattedTime) =>
        formattedTime ? `Vaxt seçin, seçilmiş vaxt: ${formattedTime}` : 'Vaxt seçin',
    fieldClearLabel: 'Dəyəri təmizlə',
    timeTableLabel: 'Vaxt seçin',
    dateTableLabel: 'Tarix seçin',
    fieldYearPlaceholder: ({ digitAmount }) => 'İ'.repeat(digitAmount),
    fieldMonthPlaceholder: ({ contentType }) => (contentType === 'letter' ? 'AAAA' : 'AA'),
    fieldDayPlaceholder: () => 'GG',
    fieldWeekDayPlaceholder: ({ contentType }) => (contentType === 'letter' ? 'GGGG' : 'GG'),
    fieldHoursPlaceholder: () => 'ss',
    fieldMinutesPlaceholder: () => 'dd',
    fieldSecondsPlaceholder: () => 'san',
    fieldMeridiemPlaceholder: () => 'gün hissəsi',
    year: 'İl',
    month: 'Ay',
    day: 'Gün',
    weekDay: 'Həftənin günü',
    hours: 'Saat',
    minutes: 'Dəqiqə',
    seconds: 'Saniyə',
    meridiem: 'Gün hissəsi',
    empty: 'Boş',
};

const datePickerLocaleText: Record<LocaleId, DatePickerLocaleText> = {
    [LocaleId.En]: enUS.components.MuiLocalizationProvider.defaultProps.localeText,
    [LocaleId.Ru]: ruRU.components.MuiLocalizationProvider.defaultProps.localeText,
    [LocaleId.Az]: azAZDatePickerLocaleText,
};

export const getDatePickerLocale = (language?: string) => {
    const locale = language?.split('-')[0] as LocaleId;
    const supportedLocale = locale in datePickerLocaleText ? locale : LocaleId.En;

    // AdapterMoment uses Moment's active global locale when building calendar values.
    // Set it explicitly here, including on initial app render when
    // the local-storage middleware has not received an action yet.
    moment.locale(supportedLocale);

    return {
        adapterLocale: supportedLocale,
        localeText: datePickerLocaleText[supportedLocale],
    };
};
